// What a person with a role over an agency reads and does, across the tenants of that agency.
import { z } from "zod";
import { MoneyDto } from "./analytics";
import { IdPageQuery, IsoDateTime, pageOf, Uuid } from "./common";
import { Role, TenantStatus } from "./identity";

/** `host`: the host's sign-in decides who is in the agency, so it only reads here. `here`: signed up directly, managed on this screen. */
export const AGENCY_PEOPLE_SOURCES = ["host", "here"] as const;
export const AgencyPeopleSource = z.enum(AGENCY_PEOPLE_SOURCES);
export type AgencyPeopleSource = z.infer<typeof AgencyPeopleSource>;

export const AgencyDto = z.object({
  id: Uuid,
  name: z.string(),
  /** The caller's role over the agency. */
  role: Role,
  people: AgencyPeopleSource,
  tenantCount: z.number().int(),
});
export type AgencyDto = z.infer<typeof AgencyDto>;

/** The figures of one client, read through that client's own scope. Null when that one read failed: the other rows still show. */
export const AgencyTenantFigures = z.object({
  /** Advertising spend of the last 30 days, per currency. Nothing is converted. */
  spend: z.array(MoneyDto),
  lastPostAt: IsoDateTime.nullable(),
  pendingApprovals: z.number().int(),
  /** Brand fields where the client replaced what the agency gives. Forbidden phrases and competitors add up, so they never appear. */
  brandOverrides: z.array(z.string()),
});
export type AgencyTenantFigures = z.infer<typeof AgencyTenantFigures>;

export const AgencyTenantRow = z.object({
  id: Uuid,
  name: z.string(),
  status: TenantStatus,
  vertical: z.string(),
  country: z.string(),
  figures: AgencyTenantFigures.nullable(),
});
export type AgencyTenantRow = z.infer<typeof AgencyTenantRow>;

export const AgencyTenantQuery = IdPageQuery;
export const AgencyTenantPage = pageOf(AgencyTenantRow);
export type AgencyTenantPage = z.infer<typeof AgencyTenantPage>;

export const AgencyMemberDto = z.object({
  id: Uuid,
  userId: Uuid,
  name: z.string(),
  /** A viewer sees who is there, and only their own address. */
  email: z.string().nullable(),
  role: Role,
  isYou: z.boolean(),
  joinedAt: IsoDateTime,
});
export type AgencyMemberDto = z.infer<typeof AgencyMemberDto>;

export const AgencyMemberPage = pageOf(AgencyMemberDto).extend({ source: AgencyPeopleSource });
export type AgencyMemberPage = z.infer<typeof AgencyMemberPage>;

export const AgencyMemberPatch = z.strictObject({ role: Role });
export type AgencyMemberPatch = z.infer<typeof AgencyMemberPatch>;

/** The agency in the operator's console, where a tenant is attached to it. */
export const AdminAgencyDto = z.object({
  id: Uuid,
  hostId: Uuid,
  hostSlug: z.string(),
  name: z.string(),
  tenantCount: z.number().int(),
  fedByHost: z.boolean(),
});
export type AdminAgencyDto = z.infer<typeof AdminAgencyDto>;

export const AdminAgencyPage = pageOf(AdminAgencyDto);
export type AdminAgencyPage = z.infer<typeof AdminAgencyPage>;

export const AdminAgencyQuery = IdPageQuery.extend({
  hostId: Uuid.optional(),
  q: z.string().trim().min(1).max(200).optional(),
});
export type AdminAgencyQuery = z.infer<typeof AdminAgencyQuery>;

export const AdminTenantAgencyPut = z.strictObject({
  /** Null detaches the tenant. */
  agencyId: Uuid.nullable(),
  reason: z.string().trim().min(3).max(300),
});
export type AdminTenantAgencyPut = z.infer<typeof AdminTenantAgencyPut>;
